/**
 * @deprecated Use payrunQueryKeys factory instead for hierarchical query invalidation.
 * These constants are kept for backward compatibility only.
 */
export const PAYRUN_QUERY_KEYS = {
  GET_PAYRUN_AMENDMENTS: 'get-payrun-amendments',
  GET_PAYRUN_WORKERS: 'get-payrun-workers',
  GET_PAYRUN_DETAIL: 'get-payrun-detail',
  GET_PAYRUN_MILESTONES: 'get-payrun-milestones',
  GET_PAYRUN_HEADER: 'get-payrun-header',
  GET_PAYRUN_CHANGE_LOG: 'get-payrun-change-log',
  GET_PAYRUN_EARNING_LINES: 'get-payrun-earning-lines',
  GET_PAYRUN_SUMMARY: 'get-payrun-summary',
  GET_PAYRUN_FILES: 'get-payrun-files',
  GET_PAYRUN_INVOICE: 'get-payrun-invoice',
  GET_PAYRUN_WORKER_DETAILS: 'get-payrun-worker-details',
  GET_PAYRUN_EARNING_LINE_DETAILS_VARIANCE: 'get-payrun-earning-line-details-variance',
  GET_PAYRUN_LIST: 'get-payrun-list',
  GET_PAYRUN_APPROVAL_LIST: 'get-payrun-approval-list',
  GET_PAYRUN_APPROVAL_DETAIL: 'get-payrun-approval-detail',
};

/**
 * Hierarchical query key factory for payrun-related queries.
 * Follows Tanstack Query best practices for query invalidation.
 *
 * Structure:
 * - ['payruns'] - Root level, invalidates ALL payrun queries
 * - ['payruns', 'list'] - All list queries
 * - ['payruns', 'list', url] - Specific list query with URL (includes params)
 * - ['payruns', 'detail'] - All detail queries
 * - ['payruns', 'detail', id, url] - Specific payrun detail with URL
 * - ['payruns', 'approvers', companyId] - All approvers for a company
 * - ['payruns', 'approvers', companyId, 'list', url] - Approvers list with URL
 * - ['payruns', 'approvers', companyId, 'detail', userId, url] - Specific approver detail with URL
 *
 * @example
 * // Invalidate all payrun queries
 * queryClient.invalidateQueries({ queryKey: payrunQueryKeys.all });
 *
 * @example
 * // Invalidate only list queries
 * queryClient.invalidateQueries({ queryKey: payrunQueryKeys.lists() });
 *
 * @example
 * // Invalidate all approvers for a specific company
 * queryClient.invalidateQueries({ queryKey: payrunQueryKeys.approvers.all('company-123') });
 */
export const payrunQueryKeys = {
  all: ['payruns'] as const,

  lists: () => [...payrunQueryKeys.all, 'list'] as const,
  list: (url: string) => [...payrunQueryKeys.lists(), url] as const,

  details: () => [...payrunQueryKeys.all, 'detail'] as const,
  detail: (id: string, url: string) => [...payrunQueryKeys.details(), id, url] as const,

  approvers: {
    all: (companyId: string) => [...payrunQueryKeys.all, 'approvers', companyId] as const,
    lists: (companyId: string) =>
      [...payrunQueryKeys.approvers.all(companyId), 'list'] as const,
    list: (companyId: string, url: string) =>
      [...payrunQueryKeys.approvers.lists(companyId), url] as const,
    details: (companyId: string) =>
      [...payrunQueryKeys.approvers.all(companyId), 'detail'] as const,
    detail: (companyId: string, userId: string, url: string) =>
      [...payrunQueryKeys.approvers.details(companyId), userId, url] as const,
  },

  assigneeHistory: (id: string, url: string) =>
    [...payrunQueryKeys.details(), id, url, 'assigneeHstory'] as const,

  externalPayruns: (id: string, url: string) =>
    [...payrunQueryKeys.details(), id, url, 'externalPayruns'] as const,
};

/**
 * @deprecated Use businessUnitQueryKeys factory instead for hierarchical query invalidation.
 * These constants are kept for backward compatibility only.
 */
export const BUSINESS_UNIT_QUERY_KEYS = {
  GET_BUSINESS_UNIT_LIST: 'get-business-unit-list',
  GET_BUSINESS_UNIT: 'get-business-unit',
};

/**
 * Hierarchical query key factory for business-unit-related queries.
 * Follows Tanstack Query best practices for targeted query invalidation.
 *
 * Structure:
 * - ['businessUnits'] - Root level, invalidates ALL business unit queries
 * - ['businessUnits', 'list'] - All list queries
 * - ['businessUnits', 'list', url] - Specific list with URL (includes params)
 * - ['businessUnits', 'detail'] - All detail queries
 * - ['businessUnits', 'detail', id] - All queries for a specific business unit
 * - ['businessUnits', 'detail', id, url] - Specific business unit detail
 * - ['businessUnits', 'detail', id, 'integrationSettings', url] - Integration settings
 *
 * @example
 * // Invalidate all business unit queries
 * queryClient.invalidateQueries({ queryKey: businessUnitQueryKeys.all });
 */
export const businessUnitQueryKeys = {
  all: ['businessUnits'] as const,

  lists: () => [...businessUnitQueryKeys.all, 'list'] as const,
  list: (url: string) => [...businessUnitQueryKeys.lists(), url] as const,

  details: () => [...businessUnitQueryKeys.all, 'detail'] as const,
  detailsById: (id: string) => [...businessUnitQueryKeys.details(), id] as const,
  detail: (id: string, url: string) =>
    [...businessUnitQueryKeys.detailsById(id), url] as const,

  integrationSettings: (id: string, url: string) =>
    [...businessUnitQueryKeys.detailsById(id), 'integrationSettings', url] as const,
};
/**
 * @deprecated Use amendmentQueryKeys factory instead for hierarchical query invalidation.
 * These constants are kept for backward compatibility only.
 */
export const AMENDMENT_QUERY_KEYS = {
  GET_AMENDMENTS: 'get-amendments',
  GET_AMENDMENT_BY_ID: 'get-amendment-by-id',
};

/**
 * Hierarchical query key factory for amendment-related queries.
 * Follows Tanstack Query best practices for targeted query invalidation.
 *
 * Structure:
 * - ['amendments'] - Root level, invalidates ALL amendment queries
 * - ['amendments', 'list'] - All list queries
 * - ['amendments', 'list', url] - Specific list with URL (includes params)
 * - ['amendments', 'detail'] - All detail queries
 * - ['amendments', 'detail', id] - All queries for a specific amendment (detail, changeLog, files)
 * - ['amendments', 'detail', id, url] - Specific amendment detail
 * - ['amendments', 'detail', id, url, 'changeLog'] - Amendment change log
 * - ['amendments', 'detail', id, url, 'files'] - Amendment files
 * - ['amendments', 'fileDownload', fileId, url] - File download
 * - ['amendments', 'changeTasks', url] - Change tasks lookup
 * - ['amendments', 'changeTaskSubTypes', url] - Change task sub-types lookup
 *
 * @example
 * // Invalidate all amendment queries
 * queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.all });
 *
 * @example
 * // Invalidate only list queries
 * queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
 *
 * @example
 * // Invalidate all queries for a specific amendment (detail + changeLog + files)
 * queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.detailsById(id) });
 */
export const amendmentQueryKeys = {
  all: ['amendments'] as const,

  lists: () => [...amendmentQueryKeys.all, 'list'] as const,
  list: (url: string) => [...amendmentQueryKeys.lists(), url] as const,

  details: () => [...amendmentQueryKeys.all, 'detail'] as const,
  detailsById: (id: string) => [...amendmentQueryKeys.details(), id] as const,
  detail: (id: string, url: string) =>
    [...amendmentQueryKeys.detailsById(id), url] as const,

  changeLog: (id: string, url: string) =>
    [...amendmentQueryKeys.detail(id, url), 'changeLog'] as const,

  files: (id: string, url: string) =>
    [...amendmentQueryKeys.detail(id, url), 'files'] as const,

  fileDownload: (fileId: string, url: string) =>
    [...amendmentQueryKeys.all, 'fileDownload', fileId, url] as const,

  changeTasks: (url: string) => [...amendmentQueryKeys.all, 'changeTasks', url] as const,

  changeTaskSubTypes: (url: string) =>
    [...amendmentQueryKeys.all, 'changeTaskSubTypes', url] as const,
};

export const payScheduleQueryKeys = {
  all: ['paySchedules'] as const,
  lists: () => [...payScheduleQueryKeys.all, 'list'] as const,
  list: (url: string) => [...payScheduleQueryKeys.lists(), url] as const,
  details: () => [...payScheduleQueryKeys.all, 'detail'] as const,
  detail: (id: string, url: string) =>
    [...payScheduleQueryKeys.details(), id, url] as const,
  baseMapping: (id: string, url: string) =>
    [...payScheduleQueryKeys.details(), id, url, 'baseMapping'] as const,
  fees: (id: string, url: string) =>
    [...payScheduleQueryKeys.details(), id, url, 'fees'] as const,
};
